/**
 * WHICH SCREEN A SIGNED-IN PLACE SHOWS, and it is the Worker's answer, never this file's.
 *
 * Every place a person reaches once signed in is routed through here, Home at `/` first, and
 * /api/me decides: signed out is the door, signed in with no home linked is the page that says to
 * call, and signed in with a home linked is the place the route names, `screen`, handed the address
 * or the mobile number she signed in with (home #11) and the way to ask again as `Signed`. The place
 * then reads itself (`components/Guarded.tsx`), and that read has the last word: a 401 there is the
 * door and a 404 the page that says to call, which is how a membership ops revokes mid-session
 * reads. Home is the holding screen until something of the household has come down.
 *
 * Until the answer arrives nothing is drawn but the page colour, so a signed-in person never sees
 * the door flash past on the way to their own screen. The answer also says until when her fresh
 * code holds, which the browser keeps for a press behind a code in any of her tabs (`lib/fresh.ts`,
 * round four, slice M3).
 *
 * After a sign-out the Worker has said yes to, the question is asked again rather than assumed; one
 * that did not go through never asks, and leaves the person where they are, saying so
 * (`components/SignOut.tsx`). Signing in happens on `/sign-in`, which comes back to `/` once the
 * Worker has set the session, and this file asks again; where she signed in from the door drawn at
 * a deeper page, Home then sends her on to that page (`lib/cameFor.ts`, P107). A door drawn after the question is asked
 * again, a sign-out's or a 401's in the middle of a session, takes the place of the screen she was
 * on, and takes the focus to its heading (`Door`), and so does the page that says to call, drawn
 * when the answer then is that no home is linked (`NoMembership`).
 *
 * AN INVITATION THAT WAITS FOR HER CODE (found on staging, 2026-11-05; `docs/seam.md` P118). The
 * answer says how many wait for a code to the address or number she signed in with, which this
 * hands the screen, and the page that says to call, with the way to ask again once a code opened
 * them (`components/InvitationWaits.tsx`): asked again with the screen still drawn, so nothing
 * blanks, and where the answer then draws another screen, the page that says to call giving way to
 * Home once a home is linked, that screen is drawn in place, with the focus on its heading.
 */
import { useEffect, useRef, useState, type ComponentType } from 'react';
import { getMe, type Me } from '../api.ts';
import type { Signed } from '../components/Guarded.tsx';
import { useHeadingFocus } from '../components/RouteFocus.tsx';
import { heardFresh } from '../lib/fresh.ts';
import { Door } from './Door.tsx';
import { NoMembership } from './NoMembership.tsx';

/** Which screen an answer of /api/me draws: the door, the page that says to call, or the place. */
function drawnBy(me: Me | null): 'door' | 'no_home' | 'place' | null {
  if (me === null) return null;
  if (!me.signedIn) return 'door';
  return me.hasMembership ? 'place' : 'no_home';
}

/** Gives the page's heading the focus once it is drawn, where `take` holds. */
function HeadingFocus({ take }: { readonly take: boolean }) {
  useHeadingFocus(take);
  return null;
}

export function Entry({ screen: Screen }: { screen: ComponentType<Signed> }) {
  const [me, setMe] = useState<Me | null>(null);
  const [asked, setAsked] = useState(0);
  /** What /api/me said last, so an answer asked again can tell whether it draws another screen. */
  const held = useRef<Me | null>(null);
  /** The screen drawn was drawn in place of another, by an answer asked again, with no move. */
  const [inPlace, setInPlace] = useState(false);

  useEffect(() => {
    let live = true;
    void getMe().then((answer) => {
      if (!live) return;
      // Until when her fresh code holds, as the Worker says it, which a press behind a code reads
      // (`lib/fresh.ts`, round four, slice M3): none where she is signed out.
      heardFresh(answer.signedIn ? answer.freshUntil : undefined);
      setInPlace(asked > 0 && drawnBy(held.current) !== drawnBy(answer));
      held.current = answer;
      setMe(answer);
    });
    return () => {
      live = false;
    };
  }, [asked]);

  const askAgain = () => {
    held.current = null;
    setMe(null);
    setAsked((count) => count + 1);
  };

  /**
   * A code opened the invitations that waited (`components/InvitationWaits.tsx`, P118): /api/me is
   * asked again with the screen still drawn, so the count reads afresh, and where the page that says
   * to call was drawn and a home is linked now, the place is drawn in its place, its heading taking
   * the focus as any page drawn in place does. A screen it leaves as it was keeps the focus where
   * it was.
   */
  const readAgain = () => setAsked((count) => count + 1);

  if (me === null) return <div className="min-h-dvh bg-surface-page" />;
  if (!me.signedIn) return <Door inPlace={inPlace} />;
  if (me.hasMembership) {
    return (
      <>
        <HeadingFocus take={inPlace} />
        <Screen
          signedInWith={me.signedInWith}
          onSignedOut={askAgain}
          invitationsWaiting={me.invitationsWaiting ?? 0}
          onInvitationsOpened={readAgain}
        />
      </>
    );
  }
  return (
    <NoMembership
      signedInWith={me.signedInWith}
      onSignedOut={askAgain}
      inPlace={inPlace}
      invitationsWaiting={me.invitationsWaiting ?? 0}
      onInvitationsOpened={readAgain}
    />
  );
}
